import { api } from './api'
import type { AuthResponse, LoginRequest, RegisterRequest, User } from '../types'
import { API_ENDPOINTS } from '../utils/constants'
import { logger } from '../utils/logger'

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await api.post<any>(API_ENDPOINTS.LOGIN, credentials)
    
    logger.debug('Raw login response from backend', {
      component: 'AuthService',
      action: 'login_response',
      data: response.data
    })
    
    // Handle different possible response formats from backend
    let authResponse: AuthResponse
    
    if (response.data.access_token) {
      // Direct format: { access_token, token_type, user_type, user }
      authResponse = {
        access_token: response.data.access_token,
        token_type: response.data.token_type || 'Bearer',
        user: response.data.user || {
          id: response.data.user_id || 'temp_id',
          email: credentials.email,
          full_name: response.data.full_name || response.data.name || 'User',
          is_active: true,
          user_type: response.data.user_type || 'company_owner'
        }
      }
      
      // Ensure user_type is set from the response
      if (response.data.user_type && authResponse.user) {
        authResponse.user.user_type = response.data.user_type
      }
    } else {
      throw new Error('Invalid login response: missing access_token')
    }
    
    // Token will be automatically attached by API interceptors
    // No need to manually set it here
    
    logger.debug('Processed auth response', {
      component: 'AuthService',
      action: 'processed_response',
      data: authResponse
    })
    
    return authResponse
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    logger.debug('Sending registration request', {
      component: 'AuthService',
      action: 'register_request',
      data: { email: data.email, full_name: data.full_name }
    })
    
    const response = await api.post<any>(API_ENDPOINTS.REGISTER, data)
    
    logger.debug('Raw register response from backend', {
      component: 'AuthService',
      action: 'register_response',
      data: response.data
    })
    
    // Handle different possible response formats from backend
    let authResponse: AuthResponse
    
    if (response.data.access_token) {
      // Direct format: { access_token, token_type, user }
      authResponse = {
        access_token: response.data.access_token,
        token_type: response.data.token_type || 'Bearer',
        user: response.data.user || {
          id: response.data.user_id || 'temp_id',
          email: data.email,
          full_name: data.full_name,
          is_active: true
        }
      }
    } else {
      throw new Error('Invalid registration response: missing access_token')
    }
    
    // Token will be automatically attached by API interceptors
    // No need to manually set it here
    
    logger.debug('Processed registration response', {
      component: 'AuthService',
      action: 'processed_register_response',
      data: authResponse
    })
    
    return authResponse
  }

  async logout(): Promise<void> {
    // For JWT authentication, logout is typically client-side only
    // since JWTs are stateless and expire naturally
    logger.debug('Performing client-side logout (JWT is stateless)', {
      component: 'AuthService',
      action: 'logout'
    })
    
    // No API call needed for JWT logout
    // Token will be removed from localStorage by auth store
    // and API interceptors will stop sending it
  }

  async getCurrentUser(): Promise<User> {
    // Check user type to determine which endpoint to use
    const userType = localStorage.getItem('user_type')
    const bgaiStorage = localStorage.getItem('bgai-auth-storage')
    
    logger.debug('🔍 getCurrentUser called', {
      component: 'AuthService',
      userType: userType,
      hasToken: !!localStorage.getItem('token'),
      localStorage: {
        user_type: localStorage.getItem('user_type'),
        token: localStorage.getItem('token') ? 'present' : 'missing',
        'bgai-auth-storage': bgaiStorage ? 'present' : 'missing'
      },
      callStack: new Error().stack?.split('\n')[1] // Show where this was called from
    })
    
    // Double-check by also looking at bgai-auth-storage
    let detectedUserType = userType
    if (!detectedUserType && bgaiStorage) {
      try {
        const parsed = JSON.parse(bgaiStorage)
        // Try both possible field names
        detectedUserType = parsed.state?.user?.user_type || parsed.state?.user?.role
        logger.debug('🔍 Detected user_type from bgai-auth-storage', {
          component: 'AuthService',
          detectedUserType,
          userObject: parsed.state?.user
        })
      } catch (e) {
        logger.warn('Failed to parse bgai-auth-storage', { component: 'AuthService' })
      }
    }
    
    // Default to company_owner if we can't determine the type
    if (!detectedUserType) {
      detectedUserType = 'company_owner'
      logger.debug('🔍 No user type found, defaulting to company_owner', {
        component: 'AuthService'
      })
    }
    
    // Force logout and re-login if user type detection is uncertain and we're getting auth errors
    // This is a temporary fix for existing users who don't have proper user_type stored
    if (!userType && !localStorage.getItem('user_type')) {
      logger.warn('🔄 User type not found in localStorage, may need re-login for proper auth', {
        component: 'AuthService',
        detectedUserType
      })
    }
    
    if (detectedUserType === 'agent') {
      logger.debug('🤖 Calling agent profile endpoint', {
        component: 'AuthService',
        endpoint: '/agents/auth/profile'
      })
      
      try {
        const response = await api.get<User>('/agents/auth/profile')

        logger.debug('✅ Agent profile response received', {
          component: 'AuthService',
          data: response.data,
          status: response.status
        })

        // Store user_type for consistency
        localStorage.setItem('user_type', 'agent')

        return response.data
      } catch (error) {
        logger.error('❌ Agent profile request failed', {
          component: 'AuthService',
          error: error instanceof Error ? error.message : 'Unknown error',
          status: (error as any)?.response?.status,
          data: (error as any)?.response?.data
        })
        throw error
      }
    } else {
      // Default to company owner endpoint for all non-agent users
      logger.debug('👤 Calling company owner profile endpoint', {
        component: 'AuthService',
        endpoint: API_ENDPOINTS.ME,
        detectedUserType,
        reason: 'Not an agent user, using company owner endpoint'
      })
      
      try {
        const response = await api.get<User>(API_ENDPOINTS.ME)

        logger.debug('✅ Company owner profile response received', {
          component: 'AuthService',
          data: response.data,
          status: response.status
        })

        // Store user_type from the response for future calls
        // This handles cases where the wrong endpoint was initially used
        const responseUserType = response.data.user_type || response.data.role || 'company_owner'
        localStorage.setItem('user_type', responseUserType)

        logger.debug('📝 Stored user_type in localStorage', {
          component: 'AuthService',
          userType: responseUserType,
          source: 'backend response'
        })

        return response.data
      } catch (error) {
        logger.error('❌ Company owner profile request failed', {
          component: 'AuthService',
          error: error instanceof Error ? error.message : 'Unknown error',
          status: (error as any)?.response?.status,
          data: (error as any)?.response?.data
        })
        throw error
      }
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    // Note: Most JWT implementations don't use refresh tokens
    // This method is kept for future compatibility
    const response = await api.post<AuthResponse>(API_ENDPOINTS.REFRESH)
    return response.data
  }

  // Legacy methods - token management now handled by API interceptors
  setAuthToken(token: string): void {
    // Token is now automatically read from localStorage by interceptors
    logger.debug('setAuthToken called - now handled by interceptors', {
      component: 'AuthService',
      action: 'legacy_method_call'
    })
  }

  clearAuthToken(): void {
    // Token clearing handled by logout in auth store
    logger.debug('clearAuthToken called - now handled by interceptors', {
      component: 'AuthService', 
      action: 'legacy_method_call'
    })
  }
}

export const authService = new AuthService()